/**
 * The firm-branded client report from the chat. The agent proposed it; the advisor fills in the cover letter
 * in the same form the FLD page uses, and submitting it is the approval: the server checks firm membership,
 * records the decision, builds the report with the evidence appendix and stores it for sign-off.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DocumentTextIcon, PencilSquareIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { firmsApi } from '../../lib/api';
import { agentApi, type ActionCardState } from '../../lib/agent';
import PartnerReportModal from '../fld/PartnerReportModal';
import DeliverableSignoff from './DeliverableSignoff';
import AgentCards from './AgentCards';
import { shell, primaryBtn, secondaryBtn, useRefresh } from './cardKit';

export default function ClientReportCard({ teamId, card, queryKey }: { teamId: string; card: ActionCardState; queryKey: unknown[] }) {
  const refresh = useRefresh(queryKey);
  const [open, setOpen] = useState(false);
  const { data: firms = [] } = useQuery({
    queryKey: ['firms', 'mine'],
    queryFn: async () => (await firmsApi.mine()).data.firms,
    staleTime: 5 * 60 * 1000,
  });
  const reject = useMutation({ mutationFn: () => agentApi.rejectAction(teamId, card.id), onSuccess: refresh });
  const analysisId = (card.params as { analysisId?: string } | null)?.analysisId ?? '';
  const gaps = card.result?.gaps ?? [];

  return (
    <div className={`${shell} border-gray-200`}>
      <div className="flex items-start gap-3">
        <DocumentTextIcon className="mt-0.5 h-5 w-5 shrink-0 text-ocean-600" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-navy-900">{card.title}</p>

          {card.status === 'PROPOSED' && (
            <>
              <p className="mt-0.5 text-xs text-gray-500">
                Your firm's branding, the analyses, observations and recommendations, and the evidence appendix. Fill in the cover letter to build it; it then goes through sign-off.
              </p>
              {firms.length === 0 ? (
                <p className="mt-2 text-xs text-amber-700">Only members of an advisory firm can prepare the client report.</p>
              ) : (
                <div className="mt-2 flex gap-2">
                  <button type="button" className={primaryBtn} onClick={() => setOpen(true)}>
                    <PencilSquareIcon className="h-4 w-4" /> Fill in the cover letter
                  </button>
                  <button type="button" className={secondaryBtn} disabled={reject.isPending} onClick={() => reject.mutate()}>Not now</button>
                </div>
              )}
            </>
          )}

          {card.status === 'DONE' && card.result?.deliverableId && (
            <>
              <DeliverableSignoff teamId={teamId} deliverableId={card.result.deliverableId} />
              {gaps.length > 0 && (
                <div className="mt-2 text-xs text-amber-800">
                  <p className="inline-flex items-center gap-1 font-medium"><ExclamationTriangleIcon className="h-4 w-4" /> Still to fill in by hand before it is final:</p>
                  <ul className="ml-5 list-disc">{gaps.map((g) => <li key={g}>{g}</li>)}</ul>
                </div>
              )}
              {card.result?.followUpActionId && <AgentCards teamId={teamId} refs={[{ kind: 'action', id: card.result.followUpActionId }]} />}
            </>
          )}
          {card.status === 'FAILED' && <p className="mt-1 text-xs text-red-700">{card.error ?? 'It could not be prepared.'}</p>}
          {card.status === 'REJECTED' && <p className="mt-1 text-xs text-gray-500">Not prepared.</p>}
        </div>
      </div>

      {firms.length > 0 && analysisId && (
        <PartnerReportModal
          isOpen={open}
          onClose={() => setOpen(false)}
          teamId={teamId}
          analysisId={analysisId}
          firms={firms}
          onSubmit={async (body) => {
            const { data } = await agentApi.approveAction(teamId, card.id, body);
            await refresh();
            return { gaps: data.card.result?.gaps ?? [] };
          }}
        />
      )}
    </div>
  );
}
